// components/modals/ChecklistSelectionModal.js
import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { checklistService } from '../../../services/supabaseService';

const ChecklistSelectionModal = ({
  isOpen,
  onClose,
  onSave,
  serviceId,
  currentSelectedChecklistIds = [],
  title = "Sélectionner des points de contrôle"
}) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChecklistIds, setSelectedChecklistIds] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Initialiser les checklists sélectionnés
  useEffect(() => {
    if (currentSelectedChecklistIds) {
      setSelectedChecklistIds([...currentSelectedChecklistIds]);
    }
  }, [currentSelectedChecklistIds]);

  // Charger les checklists
  useEffect(() => {
    if (!isOpen) return;

    const fetchChecklists = async () => {
      try {
        setLoading(true);
        const { data: checklistsData, error } = await checklistService.getAllChecklists();
        
        if (error) throw error;

        if (checklistsData) {
          const transformedChecklists = checklistsData.map(checklist => ({
            id: checklist.id,
            name: checklist.titre || 'N/A',
            code: checklist.code || 'N/A',
            description: checklist.description || '',
            category: checklist.category || 'general',
            complianceStatus: checklist.compliance_status || 'pending',
            frequency: checklist.frequency || 'monthly',
            status: checklist.status || 'active',
            itemsCount: checklist.items_count || 0,
            createdAt: new Date(checklist.created_at),
            updatedAt: new Date(checklist.updated_at)
          }));
          setChecklists(transformedChecklists);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des checklists:', err);
        setError('Erreur lors du chargement des checklists');
      } finally {
        setLoading(false);
      }
    };

    fetchChecklists();
  }, [isOpen]);

  // Filtrer les checklists
  const filteredChecklists = useMemo(() => {
    let filtered = [...checklists];

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(checklist =>
        checklist.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checklist.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checklist.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par catégorie
    if (filterCategory !== 'all') {
      filtered = filtered.filter(checklist => checklist.category === filterCategory);
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(checklist => checklist.status === filterStatus);
    }

    return filtered;
  }, [checklists, searchTerm, filterCategory, filterStatus]);

  // Gestion de la sélection
  const handleChecklistToggle = (checklistId) => {
    setSelectedChecklistIds(prev => {
      if (prev.includes(checklistId)) {
        return prev.filter(id => id !== checklistId);
      } else {
        return [...prev, checklistId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedChecklistIds.length === filteredChecklists.length) {
      setSelectedChecklistIds([]);
    } else {
      const allIds = filteredChecklists.map(checklist => checklist.id);
      setSelectedChecklistIds(allIds);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(selectedChecklistIds);
    }
    onClose();
  };

  // Helper function for status badge
  const getStatusBadge = (status) => {
    const colors = {
      'active': 'success',
      'inactive': 'muted',
      'draft': 'warning',
      'archived': 'muted'
    };
    const labels = {
      'active': 'Actif',
      'inactive': 'Inactif',
      'draft': 'Brouillon',
      'archived': 'Archivé'
    };

    const color = colors[status] || 'muted';
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border bg-${color}/10 text-${color} border-${color}/20`}>
        {labels[status] || status}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Sélectionnez les points de contrôle à associer au service
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-border space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Rechercher des points de contrôle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Toutes catégories</option>
              <option value="sécurité">Sécurité</option>
              <option value="levage">Levage</option>
              <option value="qualité">Qualité</option>
              <option value="maintenance">Maintenance</option>
              <option value="inspection">Inspection</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Tous statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="draft">Brouillon</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredChecklists.length} point{filteredChecklists.length > 1 ? 's' : ''} de contrôle trouvé{filteredChecklists.length > 1 ? 's' : ''}
            </p>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedChecklistIds.length === filteredChecklists.length && filteredChecklists.length > 0
                  ? 'Tout désélectionner'
                  : 'Tout sélectionner'}
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedChecklistIds.length} sélectionné{selectedChecklistIds.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Checklist List */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={32} className="animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Chargement des points de contrôle...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="AlertCircle" size={32} className="text-error" />
              <span className="ml-3 text-error">{error}</span>
            </div>
          ) : filteredChecklists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="ClipboardCheck" size={48} className="text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Aucun point de contrôle trouvé</h3>
              <p className="text-sm text-muted-foreground text-center">
                Ajustez vos critères de recherche ou créez un nouveau point de contrôle.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredChecklists.map((checklist) => (
                <div
                  key={checklist.id}
                  className="flex items-center space-x-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    checked={selectedChecklistIds.includes(checklist.id)}
                    onChange={() => handleChecklistToggle(checklist.id)}
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name="ClipboardCheck" size={16} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">{checklist.name}</h4>
                          <p className="text-xs text-muted-foreground">{checklist.code}</p>
                        </div>
                      </div>
                      {getStatusBadge(checklist.status)}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {checklist.description || 'Aucune description'}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Tag" size={12} />
                        <span className="capitalize">{checklist.category}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>{checklist.frequency}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="List" size={12} />
                        <span>{checklist.itemsCount} éléments</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div>
            <p className="text-sm text-foreground">
              {selectedChecklistIds.length} point{selectedChecklistIds.length > 1 ? 's' : ''} de contrôle sélectionné{selectedChecklistIds.length > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-muted-foreground">
              Cliquez sur "Associer" pour lier ces points de contrôle au service
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              onClick={handleSave}
              disabled={selectedChecklistIds.length === 0}
            >
              <Icon name="Check" size={16} className="mr-2" />
              Associer ({selectedChecklistIds.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistSelectionModal;